import {
  Avatar,
  Box,
  Button,
  CloseButton,
  Flex,
  Image,
  Input,
  Text,
  Textarea,
  VStack,
  HStack,
  useToast,
} from "@chakra-ui/react";
import { useCallback, useEffect, useState } from "react";
import Insta from "../images/insta_logo.png";
import Facebook from "../images/facebook_logo.png";
import Youtube from "../images/youtube_logo.png";
import tiktok from "../images/tictok_logo.png";
import { useNavigate, useParams } from "react-router-dom";
import { createProposal, createRank } from "../api";
import ContentModal from "./Modals/ContentModal";
import PersonaDetailModal from "./Modals/PersonaModal";
import ViewScoreModal from "./Modals/ScoreModal";

const PLATFORM_ICONS = {
  instagram: Insta,
  facebook: Facebook,
  tiktok: tiktok,
  youtube: Youtube,
};

const creatorName = (item) =>
  [item?.nickname, item?.displayName, item?.username, item?.email]
    .map((value) => (typeof value === "string" ? value.trim() : value))
    .find(Boolean) || "-";

export default function CreatorList({ creator, recommendCreator, data, page }) {
  const isCps = String(data?.billingModel ?? "").toUpperCase() === "CPS";
  const toPercentNumber = (rate) => {
    const n = Number(rate);
    if (!Number.isFinite(n)) return 0;
    return Number((n * 100).toFixed(2));
  };

  const [selectedList, setSelectedList] = useState([]);
  const [recommendedList, setRecommendedList] = useState([]);
  const [activeTab, setActiveTab] = useState("selected");
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [creatorMix, setCreatorMix] = useState([]);

  const [proposalTitle, setProposalTitle] = useState("");
  const [rate, setRate] = useState(10);
  const [proposalDesc, setProposalDesc] = useState("");
  const [expectedValue, setExpectedValue] = useState(0);
  const [isContentOpen, setIsContentOpen] = useState(false);
  const [isScoreOpen, setIsScoreOpen] = useState(false);
  const [personaOpen, setPersonaOpen] = useState(false);

  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();

  // 예상 금액 계산
  const calculateExpectedValue = useCallback((mix) => {
    if (!data) return 0;

    const rateValue = Number(rate || 0) / 100; // 10 → 0.1

    const total = isCps
      ? mix.reduce((sum, c) => sum + Number(c.manuscriptFee || 0), 0)
      : mix.reduce((sum, c) => {
          const price = c?.unitPrice || 0;
          const tv = Number(c.targetValue || 0);
          return sum + price * tv;
        }, 0);

    return Math.floor(total * (1 + rateValue));
  }, [data, isCps, rate]);

  // 숫자 포맷
  const number = (n) => (typeof n === "number" ? n.toLocaleString() : n ?? "-");

  // 부모에서 내려온 creator / recommendCreator 로 초기화
  useEffect(() => {
    const enrichForCps = (list = []) =>
      list.map((it) => ({
        ...it,
        confirmedCommissionRate:
          it?.confirmedCommissionRate ??
          toPercentNumber(it?.hopedCommissionRate),
      }));
    setSelectedList(isCps ? enrichForCps(creator ?? []) : creator ?? []);
    setRecommendedList(
      isCps ? enrichForCps(recommendCreator ?? []) : recommendCreator ?? []
    );

    if (creator?.length > 0)
      setSelectedCreator(
        isCps ? enrichForCps([creator[0]])[0] : creator[0]
      );
    else if (recommendCreator?.length > 0)
      setSelectedCreator(
        isCps ? enrichForCps([recommendCreator[0]])[0] : recommendCreator[0]
      );
  }, [creator, recommendCreator, isCps]);

  const currentList = activeTab === "selected" ? selectedList : recommendedList;

  const handleSelectCreator = (c) => setSelectedCreator(c);

  // 👉 제안서 믹스 추가 (CPUC 값 없으면 불가)
  const handleAddToMix = () => {
    if (!selectedCreator) return;

    if (isCps) {
      if (
        selectedCreator.confirmedCommissionRate == null ||
        Number(selectedCreator.confirmedCommissionRate) < 0
      ) {
        toast({
          title: "커미션율 입력 필요",
          description: "선택된 크리에이터의 확정 커미션율(%)을 입력해주세요.",
          status: "warning",
          duration: 2000,
        });
        return;
      }
      if (selectedCreator.manuscriptFee == null || Number(selectedCreator.manuscriptFee) < 0) {
        toast({
          title: "원고료 입력 필요",
          description: "선택된 크리에이터의 원고료를 입력해주세요.",
          status: "warning",
          duration: 2000,
        });
        return;
      }
    } else if (selectedCreator.targetValue == null) {
      toast({
        title: "목표 CPUC 입력 필요",
        description: "선택된 크리에이터의 목표 CPUC 값을 입력해주세요.",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    // 중복 체크 먼저 (중복이면 여기서 끝)
    const alreadyExists = creatorMix.some(
      (p) => p.creatorProfileId === selectedCreator.creatorProfileId
    );

    if (alreadyExists) {
      toast({
        title: "이미 추가됨",
        description: "이미 믹스에 있는 크리에이터입니다.",
        status: "info",
        duration: 1500,
      });
      return;
    }

    // 여기부터는 "추가 가능한 경우"만
    const newMix = [...creatorMix, selectedCreator];
    setCreatorMix(newMix);
    setExpectedValue(calculateExpectedValue(newMix));

    toast({
      title: "추가 완료",
      description: "크리에이터가 믹스에 추가되었습니다.",
      status: "success",
      duration: 1500,
    });
  };

  const handleRemoveFromMix = (creatorProfileId) => {
    const newMix = creatorMix.filter(
      (c) => c.creatorProfileId !== creatorProfileId
    );
    setCreatorMix(newMix);
    setExpectedValue(calculateExpectedValue(newMix));
  };

  useEffect(() => {
    setExpectedValue(calculateExpectedValue(creatorMix));
  }, [creatorMix, rate, isCps, calculateExpectedValue]);

  const handleSubmitProposal = async () => {
    if (!proposalTitle || !proposalDesc || !rate) {
      toast({
        title: "입력 필요",
        description: "제안서 제목과 설명을 입력해주세요.",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    if (creatorMix.length === 0) {
      toast({
        title: "크리에이터 필요",
        description: "최소 1명의 크리에이터를 믹스에 추가하세요.",
        status: "warning",
        duration: 2000,
      });
      return;
    }

    const body = {
      title: proposalTitle,
      description: proposalDesc,
      avgCostText: "",
      platformFeeRate: Number(rate) / 100, // % → 소수 변환
      creatorProfileIds: creatorMix.map((c) => ({
        creatorId: c.creatorProfileId,
        targetValue: isCps ? 0 : Number(c.targetValue ?? 0),
        unitPrice: isCps ? Number(c.confirmedCommissionRate ?? 0) / 100 : undefined,
        manuscriptFee: isCps ? Number(c.manuscriptFee ?? 0) : undefined,
      })),
    };

    try {
      await createProposal({ id, body });

      toast({
        title: "제안서가 발송되었습니다",
        description: "제안 진행중 목록으로 이동합니다.",
        status: "success",
        duration: 2000,
      });

      navigate("/suggest/offering", { replace: true });
    } catch (e) {
      console.error("제안서 생성 실패:", e);

      toast({
        title: "오류 발생",
        description:
          e?.response?.data?.detail || "제안서 발송 중 문제가 발생했습니다.",
        status: "error",
        duration: 2000,
      });
    }
  };

  const handleClick = async () => {
    if (!data?.id) return;

    try {
      const result = await createRank({ campaignId: data.id });
      return result;
    } catch (e) {
      console.error("추천 크리에이터 생성 실패", e);
    }
  };

  return (
    <>
      <Box borderRadius="16px" boxShadow="xl" py="16px" bgColor="white">
        <Box px="24px">
          <Flex gap="16px">
            {/* ------------------------------------
              좌측: 크리에이터 목록 + 탭
          ------------------------------------ */}
            <Box w="30%" h="100%">
              <Flex
                border="1px solid #E8EAEC"
                overflow="hidden"
                h="44px"
                align="center"
                bg="#F8FAFC"
              >
                <TabButton
                  active={activeTab === "selected"}
                  onClick={() => setActiveTab("selected")}
                  label={`선택된 크리에이터(${selectedList.length})`}
                />
              </Flex>

              {/* 리스트 */}
              <VStack h="100%" align="stretch" spacing="10px" overflowY="auto">
                {currentList.map((c) => {
                  const isActive =
                    selectedCreator?.creatorProfileId === c.creatorProfileId;
                  const isAdded = creatorMix.some(
                    (mix) => mix.creatorProfileId === c.creatorProfileId
                  );

                  return (
                    <Flex
                      key={c.creatorProfileId}
                      p="10px"
                      border={
                        isActive ? "2px solid #0054C0" : "1px solid #E8EAEC"
                      }
                      flexDir="column"
                      gap="12px"
                      cursor="pointer"
                      bg={isActive ? "#E9F2FF" : "white"}
                      borderRadius="10px"
                      position="relative"
                      _hover={{ bg: "#F9FAFB" }}
                      onClick={() => handleSelectCreator(c)}
                    >
                      {/* 추가됨 뱃지 */}
                      {isAdded && (
                        <Box
                          position="absolute"
                          top="6px"
                          right="6px"
                          bg="#0054C0"
                          color="white"
                          fontSize="12px"
                          px="6px"
                          py="2px"
                          borderRadius="6px"
                        >
                          추가됨
                        </Box>
                      )}

                      <Flex gap="12px" alignItems="center">
                        <Avatar name={creatorName(c)} w="48px" h="48px" />
                        <Text fontWeight="700">{creatorName(c)}</Text>
                        {PLATFORM_ICONS[c.platform?.toLowerCase()] && (
                          <Image
                            w="24px"
                            h="24px"
                            src={PLATFORM_ICONS[c.platform.toLowerCase()]}
                            alt={c.platform}
                          />
                        )}
                      </Flex>

                      <Flex gap="12px">
                        <StatBox label="팔로워" value={number(c.followers)} />
                        <StatBox label="게시물" value={number(c.posts)} />
                        <StatBox
                          label="평균 조회수"
                          value={number(c.avgViews)}
                        />
                      </Flex>
                    </Flex>
                  );
                })}
              </VStack>
              {page === "recruiting" && (
                <Flex mt="20px" w="100%" justify="center" align="center">
                  <Button
                    w="80%"
                    borderRadius="6px"
                    bg="#0054C0"
                    color="white"
                    onClick={handleClick}
                  >
                    추천 크리에이터 생성
                  </Button>
                </Flex>
              )}
            </Box>

            {/* ------------------------------------
              중앙: 크리에이터 상세 정보
          ------------------------------------ */}
            <Box w="35%" border="1px solid #E8EAEC" rounded="16px" p="16px">
              <Flex w="100%" align="center" mb="20px" justify="space-between">
                <Text fontWeight="600" fontSize="20px">
                  크리에이터 상세 정보
                </Text>
                <Button
                  fontSize="14px"
                  borderRadius="6px"
                  bg="#0054C0"
                  color="white"
                  h="28px"
                  onClick={() => setIsScoreOpen(true)}
                >
                  계정 점수
                </Button>
              </Flex>

              {!selectedCreator ? (
                <Box color="gray.500">크리에이터를 선택하세요.</Box>
              ) : (
                <>
                  <Flex gap="12px" align="center" mb="14px">
                    <Avatar name={creatorName(selectedCreator)} />
                    <Text fontWeight="700">{creatorName(selectedCreator)}</Text>
                    {PLATFORM_ICONS[
                      selectedCreator.platform?.toLowerCase()
                    ] && (
                      <Image
                        w="24px"
                        h="24px"
                        src={
                          PLATFORM_ICONS[selectedCreator.platform.toLowerCase()]
                        }
                        alt={selectedCreator.platform}
                      />
                    )}
                  </Flex>

                  <Flex gap="20px" flexDir="column">
                    <Flex gap="10px">
                      <Field label="이름" value={creatorName(selectedCreator)} />
                      <Field label="이메일" value={selectedCreator.email} />
                    </Flex>
                    <Flex gap="10px">
                      <Field
                        label="팔로워"
                        value={number(selectedCreator.followers)}
                      />
                      <Field
                        label="게시물"
                        value={number(selectedCreator.posts)}
                      />
                      <Field
                        label="평균 조회수"
                        value={number(selectedCreator.avgViews)}
                      />
                    </Flex>
                    <Flex gap="10px">
                      <Field
                        label="성별"
                        value={
                          selectedCreator?.gender === "male"
                            ? "남성"
                            : selectedCreator?.gender === "female"
                            ? "여성"
                            : "기타"
                        }
                      />
                      <Field
                        label="생년월일"
                        value={selectedCreator.birthDate}
                      />
                      <Field
                        label="국가/언어"
                        value={
                          selectedCreator.country || selectedCreator.language
                        }
                      />
                    </Flex>
                    <Flex gap="10px">
                      <Field
                        label={data?.billingModel === "CPUC" ? "CPUC" : "CPUR"}
                        value={
                          data?.billingModel === "CPUC"
                            ? selectedCreator.costPerClick
                            : selectedCreator.costPerReach
                        }
                      />
                      {!isCps && (
                        <Field label="unit price" value={selectedCreator.unitPrice} />
                      )}
                    </Flex>
                    {isCps && (
                      <Flex gap="10px">
                        <Field
                          label="희망 커미션율 (참고)"
                          value={
                            selectedCreator.hopedCommissionRate == null
                              ? "-"
                              : `${(
                                  Number(selectedCreator.hopedCommissionRate) * 100
                                ).toFixed(2)}%`
                          }
                        />
                      </Flex>
                    )}
                  </Flex>

                  {isCps && (
                    <Flex flexDir="column" my="20px">
                      <Flex gap="5px" align="center">
                        <Text fontSize="16px" fontWeight="400">
                          원고료
                        </Text>
                        <Text fontSize="12px" fontWeight={500} color="#DB5951">
                          (필수)
                        </Text>
                      </Flex>
                      <Input
                        type="number"
                        min={0}
                        placeholder="원고료 입력 (원)"
                        value={selectedCreator.manuscriptFee ?? ""}
                        onChange={(e) => {
                          const value =
                            e.target.value === "" ? null : Number(e.target.value);
                          setSelectedCreator((prev) => ({
                            ...prev,
                            manuscriptFee: value,
                          }));
                          setCreatorMix((prev) =>
                            prev.map((c) =>
                              c.creatorProfileId === selectedCreator.creatorProfileId
                                ? { ...c, manuscriptFee: value }
                                : c
                            )
                          );
                        }}
                      />
                    </Flex>
                  )}

                  <Flex flexDir="column" my="20px">
                    <Flex gap="5px" align="center">
                      <Text fontSize="16px" fontWeight="400">
                        {isCps ? "확정 커미션율 (%)" : "목표 수치"}
                      </Text>
                      <Text fontSize="12px" fontWeight={500} color="#DB5951">
                        (필수)
                      </Text>
                    </Flex>
                    <Input
                      placeholder="목표 입력"
                      value={
                        isCps
                          ? selectedCreator.confirmedCommissionRate ?? ""
                          : selectedCreator.targetValue ?? ""
                      }
                      onChange={(e) => {
                        const value =
                          e.target.value === "" ? null : Number(e.target.value);

                        setSelectedCreator((prev) => ({
                          ...prev,
                          ...(isCps
                            ? { confirmedCommissionRate: value }
                            : { targetValue: value }),
                        }));

                        setCreatorMix((prev) =>
                          prev.map((c) =>
                            c.creatorProfileId ===
                            selectedCreator.creatorProfileId
                              ? {
                                  ...c,
                                  ...(isCps
                                    ? { confirmedCommissionRate: value }
                                    : { targetValue: value }),
                                }
                              : c
                          )
                        );
                      }}
                    />
                  </Flex>

                  <Flex w="100%" gap="10px">
                    <Button
                      w="100%"
                      border="1px solid #0054C0"
                      bgColor="white"
                      color="#0054C0"
                      onClick={() => setIsContentOpen(true)}
                    >
                      콘텐츠 기획안
                    </Button>
                    <Button
                      w="100%"
                      border="1px solid #0054C0"
                      bgColor="white"
                      color="#0054C0"
                      onClick={() => setPersonaOpen(true)}
                    >
                      페르소나
                    </Button>
                  </Flex>

                  <Flex justifyContent="center">
                    <Button
                      mt="20px"
                      bgColor="#0054C0"
                      w="143px"
                      h="48px"
                      color="white"
                      onClick={handleAddToMix}
                    >
                      제안서에 추가
                    </Button>
                  </Flex>
                </>
              )}
            </Box>

            {/* ------------------------------------
              우측: 제안서 작성
          ------------------------------------ */}
            <Box
              w="35%"
              h="100%"
              border="1px solid #E8EAEC"
              rounded="16px"
              p="16px"
            >
              <Text fontWeight="700" mb="20px" fontSize="20px">
                제안서 작성
              </Text>

              <Flex gap="20px" flexDir="column">
                {/* 제목 */}
                <Box>
                  <Flex gap="5px" align="center">
                    <Text fontWeight={400} fontSize="16px" mb="6px">
                      제안서 제목
                    </Text>
                    <Text fontSize="12px" fontWeight={500} color="#DB5951">
                      (필수)
                    </Text>
                  </Flex>

                  <Input
                    placeholder="제안서 제목을 입력하세요"
                    value={proposalTitle}
                    onChange={(e) => setProposalTitle(e.target.value)}
                  />
                </Box>

                {/* 설명 */}
                <Box>
                  <Flex gap="5px" align="center">
                    <Text fontWeight={400} fontSize="16px" mb="6px">
                      제안서 설명
                    </Text>
                    <Text fontSize="12px" fontWeight={500} color="#DB5951">
                      (필수)
                    </Text>
                  </Flex>

                  <Textarea
                    placeholder="내용을 입력하세요."
                    rows={4}
                    value={proposalDesc}
                    onChange={(e) => setProposalDesc(e.target.value)}
                  />
                </Box>

                <Box>
                  <Flex gap="5px" align="center">
                    <Text fontWeight={400} fontSize="16px" mb="6px">
                      {isCps ? "플랫폼 수수료율 (%)" : "수수료율 (%)"}
                    </Text>
                    <Text fontSize="12px" fontWeight={500} color="#DB5951">
                      (필수)
                    </Text>
                  </Flex>

                  <Input
                    type="number"
                    placeholder="수수료율을 입력하세요"
                    value={rate ?? ""}
                    onChange={(e) => {
                      const onlyNumbers = e.target.value.replace(/[^0-9]/g, ""); // 숫자만 허용
                      setRate(onlyNumbers);
                    }}
                  />
                </Box>

                {/* 믹스 목록 */}
                <Box>
                  <Flex gap="5px" align="center">
                    <Text fontWeight={400} fontSize="16px" mb="6px">
                      크리에이터 믹스
                    </Text>
                    <Text fontSize="12px" fontWeight={500} color="#DB5951">
                      (필수)
                    </Text>
                  </Flex>

                  <Flex
                    minH="44px"
                    gap="6px"
                    border="1px solid #D1D4D9"
                    p="8px"
                    borderRadius="6px"
                    overflowX="auto"
                    align="center"
                  >
                    {creatorMix.length === 0 ? (
                      <Text color="#6B7280" fontSize="sm">
                        선택한 크리에이터가 여기에 추가됩니다.
                      </Text>
                    ) : (
                      creatorMix.map((c) => (
                        <HStack
                          key={c.creatorProfileId}
                          spacing="6px"
                          px="8px"
                          py="4px"
                          bg="#E9F2FF"
                          borderRadius="6px"
                          flexShrink={0}
                        >
                          <Text>{creatorName(c)}</Text>
                          <CloseButton
                            size="sm"
                            onClick={() =>
                              handleRemoveFromMix(c.creatorProfileId)
                            }
                          />
                        </HStack>
                      ))
                    )}
                  </Flex>
                  {isCps && creatorMix.length > 0 && (
                    <Box mt="8px" mb="8px">
                      {creatorMix.map((c) => (
                        <Flex
                          key={`fee-${c.creatorProfileId}`}
                          justify="space-between"
                          fontSize="13px"
                          color="#4B5563"
                          mb="4px"
                        >
                          <Text>{creatorName(c)}</Text>
                          <Text>₩{Number(c.manuscriptFee ?? 0).toLocaleString()}</Text>
                        </Flex>
                      ))}
                    </Box>
                  )}
                  <Box>
                    <Text fontWeight={400} fontSize="16px" my="6px">
                      {isCps ? "원고료 합계" : "예상 값"}
                    </Text>
                    {isCps ? (
                      <>
                        <Input
                          value={creatorMix
                            .reduce((sum, c) => sum + Number(c.manuscriptFee || 0), 0)
                            .toLocaleString()}
                          readOnly
                        />
                        <Text mt="6px" fontSize="14px" color="#0054C0">
                          수수료 포함 총액: {expectedValue.toLocaleString()}원
                        </Text>
                      </>
                    ) : (
                      <Input value={expectedValue.toLocaleString()} readOnly />
                    )}
                    <Box mt="6px">
                      {expectedValue > data.totalBudget ? (
                        <Text fontSize="14px" color="#DB5951">
                          예산을 초과했습니다! (예산:{" "}
                          {data.totalBudget.toLocaleString()} / 현재:{" "}
                          {expectedValue.toLocaleString()})
                        </Text>
                      ) : (
                        <Text fontSize="14px" color="#0054C0">
                          {expectedValue.toLocaleString()} 원 예상됩니다.
                        </Text>
                      )}
                    </Box>
                  </Box>
                </Box>

                {/* 제출 */}
                <Flex justifyContent="center">
                  <Button
                    bgColor="#0054C0"
                    w="143px"
                    h="48px"
                    color="white"
                    onClick={handleSubmitProposal}
                  >
                    제안서 작성 완료
                  </Button>
                </Flex>
              </Flex>
            </Box>
          </Flex>
        </Box>
      </Box>
      <ViewScoreModal
        isOpen={isScoreOpen}
        onClose={() => setIsScoreOpen(false)}
        profileId={selectedCreator?.creatorProfileId}
      />
      <PersonaDetailModal
        isOpen={personaOpen}
        onClose={() => setPersonaOpen(false)}
        creator={selectedCreator}
        data={data?.id}
      />
      <ContentModal
        isOpen={isContentOpen}
        onClose={() => setIsContentOpen(false)}
        creator={selectedCreator}
        data={data?.id}
      />
    </>
  );
}

/* -------- 내부 공용 컴포넌트 -------- */

function TabButton({ active, onClick, label }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      flex="1"
      h="100%"
      px="12px"
      fontSize="14px"
      fontWeight={600}
      bg={active ? "white" : "transparent"}
      color={active ? "#1F2937" : "#6B7280"}
      borderRight="1px solid #E8EAEC"
      _last={{ borderRight: "none" }}
      _hover={{ bg: active ? "white" : "#F1F5F9" }}
    >
      {label}
    </Box>
  );
}

function StatBox({ label, value }) {
  return (
    <Flex
      w="100%"
      p="8px"
      flexDir="column"
      gap="4px"
      borderRadius="8px"
      border="1px solid #D1D4D9"
      alignItems="center"
    >
      <Text color="#5D6979" fontWeight={500} fontSize="12px">
        {label}
      </Text>
      <Text color="#172940" fontWeight={600} fontSize="16px">
        {value}
      </Text>
    </Flex>
  );
}

function Field({ label, value }) {
  return (
    <Flex
      w="100%"
      borderRadius="8px"
      border="1px solid #D1D4D9"
      flexDir="column"
      justifyContent="center"
      alignItems="center"
      gap="4px"
      p="8px"
    >
      <Text fontWeight={500} fontSize="14px" color="#5D6979">
        {label}
      </Text>
      <Text color="#172940" fontWeight={600} fontSize="16px">
        {value}
      </Text>
    </Flex>
  );
}
